import React, { useState, useEffect }  from 'react';
import { useSelector } from 'react-redux';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext } from '@dnd-kit/sortable';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useFloatingTooltip } from 'hooks/useFloatingTooltip';
import APModal from 'components/Modal/APModal';
import NoteSortable from './NoteSortable';
import NoteGroupAdd from './NoteGroup.Add';

const NoteGroup = ({
  id,
  groupIndex,
  name,
  order,
  expand = true,
  renameNote,
  renameNoteGroup,
  deleteNoteGroup,
  reorderNoteGroup,
  createNoteGroup,
  updateGroupView,
  notes,
  goToNote,
  isGroup,
  viewType,
  isLast = false,
  isDragging = false,
  notesEdit,
  searchKey,
  addMode,
  setAddMode,
}) => {
  const { isOver, node, setNodeRef } = useDroppable({ id });

  const [groupEditMode, setGroupEditMode] = useState(false);
  const [tooltipMessage, setTooltipMessage] = useState('');
  const [deleteConfirmOpened, setDeleteConfirmOpened] = useState(false);
  const [hasNotes, setHasNotes] = useState(false);
  const [groupHeight, setGroupHeight] = useState(node?.current?.offsetHeight);
  const [maintainHeight, setMaintainHeight] = useState(false);

  const notesViewOptions = useSelector((state) => state.global.notesViewOptions);
  const userOrgId = useSelector((state) => state.global.userInfo.orgId);
  const projectOrgId = useSelector((state) => state.projectTools.project?.organizationId);
  const isOwner = userOrgId === projectOrgId;

  const emptyNoteGroupStyle = notes.length === 0 ? 'note-group__empty' : '';

  const showGroupNotes = () => {
    updateGroupView(id, true);
  };

  const hideGroupNotes = () => {
    updateGroupView(id, false);
  };

  const renameGroup = (e) => {
    const value = e.target.value.trim();

    if (value && value !== name) {
      renameNoteGroup({ groupId: id, groupName: value });
    }

    setGroupEditMode(false);
  };

  const deleteGroup = () => {
    deleteNoteGroup({ id });
  };

  const reorderGroup = (groupOrder) => {
    reorderNoteGroup({ id, groupOrder });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      renameGroup(e);
    }
  };

  useEffect(() => {
    if (notes.length > 0) {
      setHasNotes(true);
      setMaintainHeight(false);
    }
    if (notes.length === 0) {
      if (isDragging && node?.current) {
        setMaintainHeight(true);
      } else {
        setHasNotes(false);
        setGroupHeight(node.current.offsetHeight);
        setMaintainHeight(false);
      }
    }
  }, [notes.length, isDragging]);

  useEffect(() => {
    if (node.current) setGroupHeight(node.current.offsetHeight);
  }, [node, expand]);

  const style = {
    border: isOver ? '1px dashed grey' : '1px solid transparent',
    height: maintainHeight ? `${groupHeight}px` : undefined,
  };

  const {
    showTooltip,
    hideTooltip,
    updateTooltip,
    tooltipElement,
  } = useFloatingTooltip(tooltipMessage);

  const sortableNotes = notes.map((item, index) => (
    <React.Fragment key={item.id}>
      <NoteSortable
        key={`custom-${item.id}`}
        item={item}
        index={index}
        isSortable
        renameNote={renameNote}
        goToNote={goToNote}
        isOwner={isOwner}
        notesEdit={notesEdit}
      />
      { (index + 1) % 4 === 0 || index === notes.length - 1 || notesViewOptions.view === 'list' ? (
        <NoteGroupAdd
          createNoteGroup={createNoteGroup}
          note={item}
          index={index}
          weddingId={item.weddingId}
          groupId={item.groupId}
          notesEdit={notesEdit}
          addMode={addMode && index === notes.length - 1}
          setAddMode={setAddMode}
        />
      ) : (
        null
      )}
    </React.Fragment>
  ));

  return (
    <>
      {tooltipElement}
      <SortableContext items={notes}>
        {isGroup ? (
          <div className={clsx(
            'note-group',
            viewType ? 'grid' : null,
            { 'open': hasNotes && expand },
            groupIndex === 0 ? 'first' : null,
            isLast ? 'last' : null,
            )}
            ref={setNodeRef}
            style={style}
          >
            <div className="note-group__head">
              <div className="note-group__center">
                {groupEditMode ? (
                  <input
                    className="note-group__input"
                    placeholder="Add Section Title"
                    onBlur={renameGroup}
                    defaultValue={name}
                    onKeyDown={handleKeyDown}
                    autoFocus
                  />
                ) : (
                  <>
                    <span
                      className="note-group__title"
                      onClick={() => setGroupEditMode(true)}
                    >
                      {name}
                    </span>
                    {name !== 'Add Section Title' ? (
                      <span
                        className={clsx(
                          'note-group__count',
                          !expand && searchKey && notes?.length ? 'highlighted' : null,
                        )}
                        onClick={() => {
                          if (expand) {
                            hideGroupNotes();
                          } else {
                            showGroupNotes();
                          };
                        }}
                      >
                        { notes?.length || 0 }
                      </span>
                    ) : null}
                  </>
                )}
              </div>
              <div className="note-group__actions">
                {notesEdit ? (
                  <>
                    <i
                      className="note-group__icon delete"
                      onClick={() => setDeleteConfirmOpened(true)}
                    >
                      <FontAwesomeIcon icon={['fal', 'times-circle']} />
                    </i>
                    <i
                      className="note-group__icon move-down"
                      onClick={() => reorderGroup(order - 1)}
                      onMouseEnter={e => {
                        showTooltip();
                        updateTooltip(e);
                        setTooltipMessage('Move section down');
                      }}
                      onMouseMove={e => updateTooltip(e)}
                      onMouseLeave={hideTooltip}
                    >
                      <FontAwesomeIcon icon={['fal', 'long-arrow-down']} />
                    </i>
                    <i
                      className="note-group__icon move-up"
                      onClick={() => reorderGroup(order + 1)}
                      onMouseEnter={e => {
                        showTooltip();
                        updateTooltip(e);
                        setTooltipMessage('Move section up');
                      }}
                      onMouseMove={e => updateTooltip(e)}
                      onMouseLeave={hideTooltip}
                    >
                      <FontAwesomeIcon icon={['fal', 'long-arrow-up']} />
                    </i>
                  </>
                ) : null}
                {notes?.length ? (
                  <>
                    {expand ? (
                      <i
                        className="note-group__icon hide"
                        onClick={() => {
                          hideGroupNotes();
                          setTooltipMessage('Expand this section');
                        }}
                        onMouseEnter={e => {
                          showTooltip();
                          updateTooltip(e);
                          setTooltipMessage('Collapse this section');
                        }}
                        onMouseMove={e => updateTooltip(e)}
                        onMouseLeave={hideTooltip}
                      >
                        <FontAwesomeIcon icon={['fal', 'minus']} />
                      </i>
                    ) : (
                      <i
                        className="note-group__icon show"
                        onClick={() => {
                          showGroupNotes();
                          setTooltipMessage('Collapse this section');
                        }}
                        onMouseEnter={e => {
                          showTooltip();
                          updateTooltip(e);
                          setTooltipMessage('Expand this section');
                        }}
                        onMouseMove={e => updateTooltip(e)}
                        onMouseLeave={hideTooltip}
                      >
                        <FontAwesomeIcon icon={['fal', 'plus']} />
                      </i>
                    )}
                  </>
                ) : null}
              </div>
            </div>

            {notes?.length ? (
              <div className={clsx('note-group__body', viewType, { 'hide': !expand })}>
                {sortableNotes}
              </div>
            ) : null}
          </div>
          ) : (
            <div
              className={clsx('notes-list', viewType, emptyNoteGroupStyle)}
              ref={setNodeRef}
              style={style}
            >
              {sortableNotes}
            </div>
          )
        }

      </SortableContext>
      {deleteConfirmOpened ? (
        <APModal
          open={deleteConfirmOpened}
          className="ap-modal--confirm"
          onClose={(e) => {
            e.stopPropagation();
            setDeleteConfirmOpened(false);
          }}
          size="xs"
          hasHeader={false}
          actions={(
            <>
              <button
                type="button"
                className="button-cancel button"
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteConfirmOpened(false);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="button-delete button"
                onClick={deleteGroup}
              >
                Delete
              </button>
            </>
          )}
        >
          Deleting this section will permanently remove it from your notebook,
          however all notes will remain in place and move to the above section.
        </APModal>
      ) : null}
    </>
  );
};

export default NoteGroup;
